import appConfig from 'app.config'
import { AxiosError } from 'axios'
import i18next from 'i18next'
import { useTranslation } from 'react-i18next'
import {
	IKycCurrencyAndValues,
	kycCurrenciesAndValues,
} from '../constants/kycValues'
import { CountryA3 } from '../constants/countryTypes'
import { AllCountries } from '../constants/countryTypes'
import { TranslationFile } from 'i18n'
import queryString from 'query-string'
import { CookieType } from 'shared/constants/cookieTypes'

// If someting is added in this file it should also be added in shared/index.ts

export function getMarket() {
	switch (appConfig.country) {
		case 'de':
			return 'Germany'
		case 'no':
			return 'Norway'
		case 'at':
			return 'Austria'
		case 'se':
			return 'Sweden'
		case 'fi':
			return 'Finland'
		case 'dk':
			return 'Denmark'
		case 'ee':
			return 'Estonia'
		case 'lt':
			return 'Lithuania'
		case 'lv':
			return 'Latvia'
		case 'es':
			return 'Spain'
		case 'it':
			return 'Italy'
	}
}

export function getCountryA3() {
	switch (appConfig.country) {
		case 'de':
			return CountryA3.de
		case 'no':
			return CountryA3.no
		case 'at':
			return CountryA3.at
		case 'se':
			return CountryA3.se
		case 'fi':
			return CountryA3.fi
		case 'dk':
			return CountryA3.dk
		case 'es':
			return CountryA3.es
		case 'it':
			return CountryA3.it
		case 'ee':
			return CountryA3.ee
		case 'lt':
			return CountryA3.lt
		case 'lv':
			return CountryA3.lv
	}
}

export function getCountryA3ForCurrentMarket() {
	return CountryA3[appConfig.country as keyof typeof CountryA3]
}

export function getLanguageLocale() {
	switch (appConfig.country) {
		case 'se':
			return 'sv-SE'
		case 'fi':
			return 'fi-FI'
		case 'ee':
			return 'et-EE'
		case 'lt':
			return 'lt-LT'
		case 'lv':
			return 'lv-LV'
		case 'de':
			return 'de-DE'
		case 'no':
			return 'nb-NO'
		case 'at':
			return 'de-AT'
		case 'dk':
			return 'da-DK'
		case 'es':
			return 'es-ES'
		case 'it':
			return 'it-IT'
		default:
			return 'system'
	}
}

export function formatTime(num?: number, options?: Intl.NumberFormatOptions) {
	return num?.toLocaleString(getLanguageLocale(), {
		...options!,
		minimumIntegerDigits: 2,
	})
}

export function stringifyNum(num?: number, options?: Intl.NumberFormatOptions) {
	switch (appConfig.country) {
		case 'dk':
			return num?.toLocaleString(getLanguageLocale(), {
				...options!,
				maximumFractionDigits: 2,
				minimumFractionDigits: 0,
			})
		case 'es':
			return num?.toLocaleString(getLanguageLocale(), {
				...options!,
				maximumFractionDigits: 2,
				minimumFractionDigits: 2,
			})
		default:
			return num?.toLocaleString(getLanguageLocale(), {
				minimumFractionDigits: 2,
				...options!,
			})
	}
}

export function stringifyBooleanAsUi(bool: boolean) {
	if (bool) {
		return 'Yes'
	} else {
		return 'No'
	}
}

export function parseStringifedBoolean(value: string) {
	if (value === 'Yes') {
		return true
	} else if (value === 'No') {
		return false
	} else {
		return undefined
	}
}

export function checkIsMobile() {
	// eslint-disable-next-line
	return (
		/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(
			navigator.userAgent,
		) ||
		// eslint-disable-next-line
		/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
			navigator.userAgent.substr(0, 4),
		) ||
		window.innerWidth <= 845
	)
}

export function checkIsTablet() {
	return (
		checkIsMobile() && window.innerWidth >= 768 && window.innerWidth <= 1366
	)
}

export function getCurrency() {
	switch (appConfig.country) {
		case 'se':
			return 'SEK'
		case 'fi':
		case 'ee':
		case 'de':
		case 'at':
		case 'es':
		case 'lt':
		case 'lv':
		case 'it':
			return 'EUR'
		case 'no':
			return 'NOK'
		case 'dk':
			return 'DKK'
	}
}

export const isIOS = () => {
	return (
		[
			'iPad Simulator',
			'iPhone Simulator',
			'iPod Simulator',
			'iPad',
			'iPhone',
			'iPod',
		].includes(navigator.platform) ||
		// iPad on iOS 13 detection
		(navigator.userAgent.includes('Mac') && 'ontouchend' in document)
	)
}

export const isSafariBrowser = () => {
	const isChrome = navigator.userAgent.indexOf('Chrome') > -1
	const isSafari = navigator.userAgent.indexOf('Safari') > -1

	return isSafari && !isChrome // chrome got safari in it as well...
}

export async function waitForLanguage(): Promise<string> {
	if (i18next.language) return Promise.resolve(i18next.language)

	await new Promise((resolve) => setTimeout(resolve, 100))
	return waitForLanguage()
}

export function getErrorWithFullMessage(error: Error | AxiosError) {
	console.warn(error)

	if (typeof error !== 'object') {
		return error
	}

	if ('isAxiosError' in error && error.response && error.response.data) {
		return new Error(
			error.response.status < 500 ? error.response.data : 'Network Error',
		)
	}

	return new Error(error.message)
}

export const GetTranslationString = (
	key: any,
	countrySpecVal: any,
	propertyName: string,
) => {
	const { t } = useTranslation(TranslationFile.Dropdown)
	// eslint-disable-next-line eqeqeq
	if (key == 0)
		return t(propertyName + 'First', {
			value: countrySpecVal[propertyName][key].toLocaleString(
				appConfig.country,
			),
			currency: countrySpecVal.currency,
		})
	// eslint-disable-next-line eqeqeq
	if (key == countrySpecVal[propertyName].length - 1)
		return t(propertyName + 'Last', {
			value: (countrySpecVal[propertyName][key] - 1).toLocaleString(
				appConfig.country,
			),
			currency: countrySpecVal.currency,
		})
	return t(propertyName, {
		value: `${(
			countrySpecVal[propertyName][key.valueOf() - 1] + 1
		).toLocaleString(appConfig.country)} - ${countrySpecVal[propertyName][
			key
		].toLocaleString(appConfig.country)}`,
		currency: countrySpecVal.currency,
	})
}

/**
 * Gets and sets all countries from countrytypes.tsx for dropdowns listing AllCountries. Sort using market specific sorting if one is available.
 * @returns
 */
export const getAllCountriesUsingMarketSpecificSorting = (market: string) => {
	let options = GetAllCountriesWithTranslation(market)

	if (market === 'ee') {
		options.sort(marketSortEE)
	}

	return options
}

const marketSpecificSort = (
	a: any,
	b: any,
	coutrySpecificSortingOrder: string[],
): number => {
	let aIndex = coutrySpecificSortingOrder.indexOf(a.value)
	let bIndex = coutrySpecificSortingOrder.indexOf(b.value)

	if (aIndex === bIndex) return 0

	if (aIndex < bIndex) return aIndex > -1 ? -1 : 1

	return bIndex > -1 ? 1 : -1
}

const marketSortEE = (a: any, b: any): number => {
	//for "ee" market we need countries to be sorted in following way EE, LV, LT, FI, followed by the rest of the countries
	let coutrySpecificSortingOrderEE = ['EE', 'LV', 'LT', 'FI']

	return marketSpecificSort(a, b, coutrySpecificSortingOrderEE)
}

/**
 * Gets and sets all countries from countrytypes.tsx for dropdowns listing AllCountries.
 * Translates the countries from locales\{country}\countries.json.
 * Sorts alphabetically but also sets country of chosen market as first choice in menu.
 * @param market
 * @returns
 */
export const GetAllCountriesWithTranslation = (market: string) => {
	const { t } = useTranslation([TranslationFile.Countries])
	let options: any = []
	let countriesUnsorted: any = []
	let countriesSorted: any = []
	let countriesFiltered: any = []

	Object.keys(AllCountries).map((key: string) =>
		countriesUnsorted.push({
			value: key,
			label: t(`${AllCountries[key as keyof typeof AllCountries]}`),
		}),
	)
	options.push(
		countriesUnsorted.find(
			(item: { value: string; label: string }) =>
				item.value === market.toUpperCase(),
		),
	)
	countriesFiltered = countriesUnsorted.filter(
		(item: { value: string; label: string }) =>
			item.value !== market.toUpperCase(),
	)
	countriesSorted = countriesFiltered.sort(
		(
			a: { value: string; label: string },
			b: { value: string; label: string },
		) => {
			const countryA = a.label
			const countryB = b.label
			if (
				countryA.localeCompare(countryB, market) <
				countryB.localeCompare(countryA, market)
			) {
				return -1
			}
			if (
				countryA.localeCompare(countryB, market) >
				countryB.localeCompare(countryA, market)
			) {
				return 1
			}
			return 0
		},
	)
	options.push(...countriesSorted)
	return options
}

/**
 * Gets and sets country specific values and amounts stated in
 * countryValues.tsx for dropdown alternatives with different spans
 * from locales\{country}\dropdown.json.
 * @param translationSource
 * @param propertyName
 * @returns
 */
export const CountrySpecificValues = (
	translationSource = '',
	propertyName: string,
) => {
	// eslint-disable-next-line
	const { t } = useTranslation(translationSource)
	let countryData = kycCurrenciesAndValues.find(
		(e) => e.country === appConfig.country,
	) as IKycCurrencyAndValues

	let temp: any = []
	let listOfProperties = Object.entries(countryData)
	for (const [key, value] of listOfProperties) {
		if (key === propertyName)
			// eslint-disable-next-line
			value.map((item: any, key: any) => {
				temp.push({
					value: value[key],
					label: GetTranslationString(key, countryData, propertyName),
				})
			})
	}
	return temp
}

/**
 * Returns country specific values in same order as set in countryValues.tsx,
 * otherwise all values from enum are returned translated
 * from locales\{country}\dropdown.json.
 * @param enumType
 * @param propertyName
 * @param translationSource
 * @returns
 */
export const FormatEnumToList = (
	enumType: any,
	propertyName: string,
	translationSource?: TranslationFile | '',
) => {
	const { t } = useTranslation(translationSource)
	let keys = Object.keys(enumType)
	const { isSpecific, val } = hasCountrySpecificValues(propertyName)
	let temp: any[] = []
	if (isSpecific)
		val.map((key: number) =>
			temp.push({ value: enumType[key], label: t(enumType[key]) }),
		)
	else
		keys.filter(StringIsNotNumber).map((key: string) =>
			temp.push({ value: key, label: t(key.toString()) }),
		)
	return temp
}

export function StringIsNotNumber(value: string) {
	return isNaN(Number(value)) === true
}

const hasCountrySpecificValues = (propertyName: string) => {
	let countryData = kycCurrenciesAndValues.find(
		(e) => e.country === appConfig.country,
	) as IKycCurrencyAndValues
	let properties = Object.entries(countryData)
	for (const [key, val] of properties) {
		if (key === propertyName) {
			return { isSpecific: true, val }
		}
	}
	return { isSpecific: false, undefined }
}

export function getQueryParams(): {
	ReturnUrl: string
	SigningRequestId?: string
	[key: string]: any
} {
	const { ReturnUrl } = queryString.parse(window.location.search) as {
		ReturnUrl: string
	}

	if (ReturnUrl) {
		const url = new URL(decodeURIComponent(ReturnUrl))
		const params: any = queryString.parse(url.search)

		return { ...params, ReturnUrl }
	}

	return { ReturnUrl }
}

export function getMobileOperatingSystem() {
	var userAgent = navigator.userAgent || navigator.vendor
	if (/android/i.test(userAgent)) {
		return 'Android'
	}
	if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
		return 'iOS'
	}
	return 'unknown'
}

export function downloadBlob(blob: Blob, name: string) {
	const data = window.URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.style.display = 'none'
	link.href = data
	link.download = name
	document.body.appendChild(link)
	link.click()
}

export function showBlob(
	blob: Blob,
	windowRef: Window | null,
	fileName: string = 'file.pdf',
) {
	var URL = window.URL || window.webkitURL
	const data = URL.createObjectURL(blob)

	if (windowRef) {
		windowRef.location.href = data
	} else {
		downloadBlob(blob, fileName)
	}

	setTimeout(function () {
		// For Firefox it is necessary to delay revoking the ObjectURL
		URL.revokeObjectURL(data)
	}, 100)
}

export function validateOtpCode(otpCode: string) {
	if (!new RegExp('^\\d{6}$').test(otpCode)) return false

	return true
}

export function validateBankAccountNumber(
	directDebitAccount: string,
	directDebitClearingNumber: string,
	country: string,
) {
	let isValid = true
	if (
		directDebitAccount === undefined ||
		directDebitClearingNumber === undefined
	)
		return !isValid
	switch (country) {
		case 'dk':
			if (
				directDebitClearingNumber.length === 4 &&
				directDebitAccount.length >= 7 &&
				directDebitAccount.length <= 10
			) {
				return isValid
			} else {
				return !isValid
			}

		case 'ee':
		case 'lt':
		case 'sv':
		case 'fi':
		case 'pl':
		case 'de':
		case 'no':
		case 'lv':
		default:
			return !isValid
	}
}

/**
 * Get name of key (like in C# the master tier)
 * @param key - The key of T to get the name
 * @param instance - Optional way of setting T by object instead of <T>
 * @returns Value of key of T
 */
export function nameof<T>(key: keyof T, instance?: T): keyof T {
	return key
}

/**
 * Make value vocative
 * @param value
 * @returns vocatived name
 */
export function setVocative(value: string) {
	if (!value) return value

	let tempValue = value
	const vocative = {
		as: 'ai',
		ė: 'e',
		is: 'i',
		us: 'au',
		ys: 'y',
	}

	Object.keys(vocative).forEach((x) => {
		if (!tempValue.endsWith(x)) return

		const voc = vocative[x as keyof typeof vocative]
		tempValue = tempValue.substring(0, tempValue.lastIndexOf(x)) + voc
	})

	return tempValue
}

export interface ICurrencyInfo {
	name: string
	symbol: string
}

const currencyData: Record<string, ICurrencyInfo> = {
	SEK: { name: 'Swedish Krona', symbol: 'kr' },
	DKK: { name: 'Danish Krone', symbol: 'kr' },
	EUR: { name: 'Euro', symbol: '€' },
	USD: { name: 'American Dollar', symbol: '$' },
}

export const getCurrencyInfo = (currencyCode: string) => {
	const currencyInfo = currencyData[currencyCode]
	if (currencyInfo) {
		return `${currencyInfo.name} (${currencyInfo.symbol})`
	} else {
		return ''
	}
}

export const sortString = (a: string, b: string, sort: 'asc' | 'desc') => {
	if (sort === 'asc') {
		if (a > b) return -1
		if (a < b) return 1
		return 0
	} else {
		if (a < b) return -1
		if (a > b) return 1
		return 0
	}
}

export const getCustomerInfoCookieName = () : string => {
	//if customer have both loans and deposits loan customer info to be used
	let cookieName = CookieType.CustomerInfo
	//set to deposit customer info only if customer have deposits and does not have loans
	if(!sessionStorage.getItem(CookieType.LoanCustomer) && sessionStorage.getItem(CookieType.DepositCustomer))
		cookieName = CookieType.DepositCustomerInfo

	return cookieName
}
